import React, { useEffect, useState } from 'react';
import { Product } from '../types/Product';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface FilterBarProps {
  filters: {
    mainCategory: string;
    subCategory: string;
    department: string;
    priceStatus: string;
    stockStatus: string;
    dateRange: string;
    search: string;
  };
  onFilterChange: (key: string, value: string) => void;
  products: Product[];
}

const FilterBar: React.FC<FilterBarProps> = ({ filters, onFilterChange, products }) => {
  const [categories, setCategories] = useState<{ main: string[], sub: string[] }>({ main: [], sub: [] });

  useEffect(() => {
    const mainCats = Array.from(new Set(products.map(p => p.categoryInfo?.mainCategory).filter(Boolean)));
    const subCats = Array.from(new Set(products.map(p => p.categoryInfo?.subCategory).filter(Boolean)));
    setCategories({ main: mainCats as string[], sub: subCats as string[] });
  }, [products]);

  return (
    <div className="p-6 border-b border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
      </div>
      
      {/* İlk satır filtreler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ana Grup</label>
          <select
            value={filters.mainCategory}
            onChange={(e) => onFilterChange('mainCategory', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Tümü</option>
            {categories.main.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Alt Grup</label>
          <select
            value={filters.subCategory}
            onChange={(e) => onFilterChange('subCategory', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Tümü</option>
            {categories.sub.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Departman</label>
          <select
            value={filters.department}
            onChange={(e) => onFilterChange('department', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Tümü</option>
            <option value="production_entry">Üretim (SKU Bekliyor)</option>
            <option value="nebim_entry">Nebim (İşlemde)</option>
            <option value="barcode_sent">Üreticiye Gönderim</option>
            <option value="stock_entry">Stok Girişi</option>
            <option value="api_transfer">ERP Aktarım</option>
            <option value="content_entry">İçerik Girişi</option>
            <option value="category_setup">Kategorileme</option>
            <option value="image_upload">Görsel Yükleme</option>
            <option value="publish_pending">Yayın Bekliyor</option>
            <option value="published">Yayınlandı</option>
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Arama</label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              placeholder="Ürün adı, SKU, barkod..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      </div>
      
      {/* İkinci satır filtreler */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fiyat Durumu</label>
          <select
            value={filters.priceStatus}
            onChange={(e) => onFilterChange('priceStatus', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Tümü</option>
            <option value="discounted">İndirimli Ürünler</option>
            <option value="no_discount">İndirimsiz Ürünler</option>
            <option value="no_price">Fiyatsız Ürünler</option>
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Stok Durumu</label>
          <select
            value={filters.stockStatus}
            onChange={(e) => onFilterChange('stockStatus', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Tümü</option>
            <option value="in_stock">Stokta Var</option>
            <option value="out_of_stock">Stok Yok</option>
            <option value="pending">Beklemede</option>
          </select>
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tarih Aralığı</label>
          <select
            value={filters.dateRange}
            onChange={(e) => onFilterChange('dateRange', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Tümü</option>
            <option value="today">Bugün</option>
            <option value="week">Bu Hafta</option>
            <option value="month">Bu Ay</option>
          </select>
        </div>
        
        <div className="flex items-end">
          <button
            onClick={() => {
              Object.keys(filters).forEach(key => onFilterChange(key, ''));
            }}
            className="w-full px-3 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors text-sm"
          >
            Filtreleri Temizle
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
